import { Component, inject, OnInit, TemplateRef } from '@angular/core';
import { NZ_MODAL_DATA, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { ValidationResponse } from '../../models/query-interface';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { AuthService } from '../../../shared/services/auth.service';
import { ApiService } from '../../../shared/services/api.service';
import { AppService } from '../../../shared/services/app.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { FormControl, UntypedFormControl, Validators } from '@angular/forms';
import { AT_LEAST_ONE_ALPHABET } from '../../../shared/utils/constants';
import {
  ApprovalStatus,
  ApproveQueryInput,
  ApproveQueryRecordMutationVariables,
  CleanupSyntheticResultMutation,
  CleanupSyntheticResultMutationVariables,
  Engagements,
  QueryRecord,
  Status,
  SyntheticActions,
} from '../../../../API';
import { ApproveQueryRecord, CleanupSyntheticResult } from '../../../../graphql/mutations';

@Component({
  selector: 'app-view-details-modal',
  templateUrl: './view-details-modal.component.html',
  styleUrl: './view-details-modal.component.scss',
})
export class ViewDetailsModalComponent implements OnInit {
  readonly queryRecord: QueryRecord = inject(NZ_MODAL_DATA);

  userRole: any;
  rejectModalRef!: NzModalRef;
  rejectReason: UntypedFormControl = new FormControl('', [
    Validators.required,
    Validators.minLength(5),
    Validators.pattern(AT_LEAST_ONE_ALPHABET),
  ]);
  isLoading: boolean = false;
  readonly approveQueryHistoryAction: string[] = ['app.dcr-portal.query-history.action.approve'];
  readonly rejectQueryHistoryAction: string[] = ['app.dcr-portal.query-history.action.reject'];
  engagement?: string;
  username?: string;
  userOrgType: string;

  constructor(
    private userManagementService: UserManagementService,
    private modalService: NzModalService,
    private authService: AuthService,
    private apiService: ApiService,
    private appService: AppService,
    private nzMessageService: NzMessageService,
  ) {
    this.username = this.userManagementService.getEmail();
    this.userOrgType = this.userManagementService.getOrgType();
  }

  ngOnInit() {
    this.userRole = this.userManagementService.getUserRole();
    this.userOrgType = this.userManagementService.getOrgType();

    /** subscribe for engagement changes */
    this.appService.activeEngagement$.subscribe((engagements: Engagements) => {
      this.engagement = engagements?.engagement;
    });
  }

  canApproveOrReject(queryData: QueryRecord): boolean {
    if (!this.appService.isEngagementActive) return false;
    const { orgType, approvalStatus, validationStatus } = queryData;

    return (
      this.authService.hasEntitlements(this.approveQueryHistoryAction) &&
      approvalStatus?.toString() === ApprovalStatus.PENDING &&
      validationStatus === Status.SUCCESS &&
      orgType !== this.userOrgType
    );
  }

  approveQueryRecord(queryId: string) {
    this.modalService.confirm({
      nzTitle: 'Approve Query Record',
      nzContent: 'Are you sure?',
      nzOkText: 'Approve',
      nzCancelText: 'Cancel',
      nzOnOk: () => this.handleStatusUpdate(queryId, ApprovalStatus.APPROVED),
    });
  }

  rejectQueryRecord(queryId: string, rejectModalTemplateRef: TemplateRef<void>) {
    this.rejectReason.reset();
    this.rejectModalRef = this.modalService.create({
      nzTitle: 'Reject Query Record',
      nzContent: rejectModalTemplateRef,
      nzOnOk: () => this.handleStatusUpdate(queryId, ApprovalStatus.REJECTED),
      nzFooter: null,
    });
  }

  handleStatusUpdate(queryId: string, status: ApprovalStatus) {
    this.isLoading = true;
    this.cleanSyntheticResult(queryId)
      .then((cleanResult) => {
        if (cleanResult.errors?.length) {
          this.nzMessageService.error('Failed to clean up synthetic query result');
          return;
        }
      })
      .then(() => this.approveOrReject(queryId, status))
      .then((result) => {
        if (result.errors?.length) {
          if (status === ApprovalStatus.APPROVED) {
            this.nzMessageService.error('Failed to Approve query');
          } else {
            this.nzMessageService.error('Failed to Reject query');
          }
          return;
        }

        if (status === ApprovalStatus.APPROVED) {
          this.nzMessageService.success('Query Approved Successfully.');
        } else {
          this.nzMessageService.success('Query Rejected Successfully.');
        }
      })
      .catch((error) => {
        this.nzMessageService.error(error?.message ?? 'Failed to update query status.');
      })
      .finally(() => {
        this.isLoading = false;
        this.modalService.closeAll();
      });
  }

  get validationLogs(): ValidationResponse | any {
    try {
      return JSON.parse(this.queryRecord?.validationLogs || '');
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // handling invalid validationJsonLogs. and returning {} to avoid UI breaking
      return {};
    }
  }

  cleanSyntheticResult(queryId: string): Promise<any> {
    return this.apiService.query<CleanupSyntheticResultMutation, CleanupSyntheticResultMutationVariables>(
      CleanupSyntheticResult,
      {
        syntheticDataInput: {
          action: SyntheticActions.CLEANUP_RESULT,
          engagement: this.engagement!,
          queryId: queryId,
        },
      },
    );
  }

  approveOrReject(queryId: string, status: ApprovalStatus): Promise<any> {
    const approveQueryRecord: ApproveQueryRecordMutationVariables = {
      updateQueryRequest: {
        approvalStatus: status,
        approvedBy: this.username!,
        engagement: this.engagement!,
        lastModifiedBy: this.username!,
        queryId: queryId,
        rejectReason: status === ApprovalStatus.REJECTED ? this.rejectReason.value : null,
      },
    };
    return this.apiService.query<ApproveQueryInput, ApproveQueryRecordMutationVariables>(
      ApproveQueryRecord,
      approveQueryRecord,
    );
  }
}
